import { useState } from 'react'
import { Button, EmptyState } from '@teach/ui'
import { Receipt } from 'lucide-react'
import type { CreditsHistoryItem } from '@/features/ai/creditsApi'
import { useHistoryPage } from './useHistoryPage'
import { HistorySkeleton } from './HistorySkeleton'
import { HistoryRow } from './HistoryRow'
import { HistoryHeader } from './HistoryHeader'
import { HistoryPager } from './HistoryPager'
import { HistoryDetailDialog } from './HistoryDetailDialog'
import { useMessages } from '@/i18n'
import { messages } from './messages'

export const HISTORY_LIMIT = 20

/**
 * История движений. Резерва в ней нет по построению, поэтому столбца «остаток»
 * тут тоже нет: `balanceAfter` — снимок, и у автора с двумя прогонами сразу
 * соседние значения честно не сойдутся. Поле показывается только внутри
 * подробностей одной операции.
 */
interface Props {
  /**
   * Кап высоты с внутренней прокруткой — для окна кредитов (там без него список
   * уезжает за вьюпорт). На странице «Использование» прокручивается сама
   * страница, и вложенная полоса только мешает — там `false`.
   */
  capHeight?: boolean
}

export const HistoryList = ({ capHeight = true }: Props) => {
  const m = useMessages(messages)
  const [page, setPage] = useState(1)
  const [opened, setOpened] = useState<CreditsHistoryItem | null>(null)
  const { data, isLoading, isError, refetch, switching } = useHistoryPage(page, HISTORY_LIMIT)

  // Покупки — во вкладке «Платежи» с суммой и статусом заказа; здесь, в
  // «Расходе», они дублировали бы её. Фильтр на клиенте: у ручки нет параметра
  // типа операции (вопрос Пете), поэтому страница может оказаться короче лимита.
  const items = data?.items.filter((item) => item.operation !== 'purchase') ?? []

  if (isLoading) return <HistorySkeleton rows={3} />

  // Отказ объясняется и даёт повтор: ручка в списке молчащих, тоста не будет,
  // а вечный скелет выглядел бы как бесконечная загрузка.
  if (isError)
    return (
      <div className="space-y-2 rounded-xl border border-warning/30 bg-warning/10 p-3 text-xs">
        <p>{m.historyFailed}</p>
        <Button variant="outline" size="sm" onClick={() => void refetch()}>
          {m.retry}
        </Button>
      </div>
    )

  // Страница за концом списка — не «движений нет»: пагинатор в этой ветке не
  // рисуется, и вернуться было бы нечем, пока диалог не закроют. Дойти сюда
  // кликами нельзя — «следующая» гаснет на `page >= pages`; вход один:
  // серверный `pages` разъехался со страницей, которую мы уже держим в стейте
  // (история протухла между запросами).
  if (data && items.length === 0 && page > 1)
    return (
      <div className="space-y-2 py-6 text-center text-xs text-muted-foreground">
        <p>{m.emptyPage}</p>
        <Button variant="outline" size="sm" onClick={() => setPage(1)}>
          {m.toHistoryStart}
        </Button>
      </div>
    )

  if (!data || items.length === 0)
    return (
      <EmptyState
        icon={Receipt}
        className="min-h-80"
        title={m.emptyTitle}
        description={m.emptyDescription}
      />
    )

  return (
    <div>
      {/* Кап высоты — на САМОМ списке, а не на диалоге: `DialogContent` из
          `@teach/ui` ни `max-h`, ни `overflow` не задаёт, контент центрирован
          трансформом, и двадцать строк уезжают за вьюпорт вместе с
          пагинатором — прокрутить их нечем. Приём тот же, что у
          `ValidationErrorsDialog`. */}
      <HistoryHeader />
      <div className={capHeight ? 'max-h-[50vh] space-y-1 overflow-y-auto' : 'space-y-1'}>
        {switching ? (
          <HistorySkeleton />
        ) : (
          items.map((item) => <HistoryRow key={item.id} item={item} onOpen={setOpened} />)
        )}
      </div>
      {/* Номер — из стейта, а не из ответа: при переходе он меняется сразу. */}
      <HistoryPager page={page} pages={data.pages} onPageChange={setPage} />
      <HistoryDetailDialog item={opened} onClose={() => setOpened(null)} />
    </div>
  )
}
